import { useMemo, useState } from 'react';
import {
  api,
  type ManagedVpatchPublicationInput,
  type ManagedVpatchPublicationResult,
} from '../api/client';

type Review = { ok: true; input: ManagedVpatchPublicationInput } | { ok: false; reason: string };

/**
 * Platform-admin publication surface for a separately reviewed managed-vpatch packet.
 *
 * This intentionally does not try to author a rule. It exposes the existing API gate in the native
 * console, preserves the exact submitted packet for review, and refuses anything that is not the
 * monitor-first shape. The API still performs the complete catalog, predicate, timestamp, version,
 * and authorization validation before it writes anything.
 */
export function ManagedVpatchPublisher({ onPublished }: { onPublished?: () => void }) {
  const [packet, setPacket] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ManagedVpatchPublicationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const review = useMemo(() => reviewPacket(packet), [packet]);

  async function publish() {
    if (!review.ok) return;
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      const published = await api.publishManagedVpatch(review.input);
      setResult(published);
      onPublished?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The patch could not be published.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="managed-vpatch-publisher">
      <summary>Publish a reviewed managed patch</summary>
      <p className="muted">
        Paste the exact reviewed API packet. The console accepts only an enabled, global,{' '}
        <strong>monitor-only log</strong> rule with the exact typed confirmation. The API then
        revalidates its catalog link, predicate, version, and timestamp before writing anything.
      </p>
      <label htmlFor="managed-vpatch-publication">Reviewed publication JSON</label>
      <textarea
        id="managed-vpatch-publication"
        className="mono"
        rows={14}
        spellCheck={false}
        value={packet}
        onChange={(event) => {
          setPacket(event.target.value);
          setResult(null);
          setError(null);
        }}
        placeholder='{"catalogId":"…","target":{…},"rule":{…},"confirm":"publish-managed-vpatch:…"}'
      />
      {!review.ok && packet.trim() !== '' && <p className="warnbar">{review.reason}</p>}
      <button type="button" disabled={busy || !review.ok} onClick={() => void publish()}>
        {busy ? 'Publishing…' : 'Publish monitor-only patch'}
      </button>
      {error && <p className="warnbar">{error} The reviewed packet has been preserved.</p>}
      {result && (
        <p className="okbar">
          Published <code>{result.ruleId}</code> v{result.version} in monitor-only log mode. Matched{' '}
          {result.matchedSites} site{result.matchedSites === 1 ? '' : 's'}; distributed to{' '}
          {result.distributedSites}.
        </p>
      )}
    </details>
  );
}

function reviewPacket(packet: string): Review {
  if (packet.trim() === '') return { ok: false, reason: 'Paste the reviewed publication packet.' };

  let value: unknown;
  try {
    value = JSON.parse(packet) as unknown;
  } catch {
    return { ok: false, reason: 'The publication packet is not valid JSON.' };
  }

  if (!isRecord(value)) return { ok: false, reason: 'The publication packet must be an object.' };
  const { catalogId, target, rule, confirm } = value;
  if (typeof catalogId !== 'string' || catalogId.trim() === '') {
    return { ok: false, reason: 'The publication packet needs a catalogId.' };
  }
  if (!isRecord(target)) {
    return { ok: false, reason: 'The publication packet needs a target object.' };
  }
  if (!isRecord(rule)) return { ok: false, reason: 'The publication packet needs a rule object.' };
  if (typeof rule.id !== 'string' || rule.id.trim() === '') {
    return { ok: false, reason: 'The managed patch rule needs an id.' };
  }
  if (!Number.isInteger(rule.version) || Number(rule.version) < 1) {
    return { ok: false, reason: 'The managed patch rule needs a positive integer version.' };
  }
  if (rule.type !== 'vpatch' || rule.enabled !== true || rule.scope !== 'global') {
    return {
      ok: false,
      reason: 'Only an enabled, global managed-vpatch rule can be published here.',
    };
  }
  if (!isRecord(rule.action) || rule.action.kind !== 'log') {
    return { ok: false, reason: 'Managed patches must begin in monitor-only log mode.' };
  }
  const expected = `publish-managed-vpatch:${catalogId}:${rule.id}:v${String(rule.version)}`;
  if (confirm !== expected) {
    return {
      ok: false,
      reason: 'The typed confirmation does not match this catalog, rule, and version.',
    };
  }

  return {
    ok: true,
    input: value as unknown as ManagedVpatchPublicationInput,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
